/**
 * Where each tenant's container lives, for the calls that cannot go through
 * the control-plane forwarder: the chat WebSocket, and the "Open portal" link.
 *
 * Source: `container_url` on the tenant status document, registered by the
 * tenant container at startup. Cached per tenant; a null answer is retried on
 * the next ask (the tenant may still be onboarding), and `invalidate` drops a
 * cached address after a forwarder 502.
 *
 * Uses `fetch` rather than the axios client on purpose: the chat hook imports
 * this module, and its import graph must stay small.
 */
import { API_BASE_URL } from 'config/api';
import AuthService from './AuthService';
import type { SingleTenantStatusResponse } from 'types/Tenant';

const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();

async function fetchContainerUrl(tenantId: string): Promise<string | null> {
  const token = AuthService.getAuthToken();
  if (!token) return null;
  const resp = await fetch(`${API_BASE_URL}/config/tenant-status/${encodeURIComponent(tenantId)}`, {
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
      // The control plane answers this itself; the header only keeps the
      // request unambiguous for the tenant guard.
      'X-Tenant-Id': tenantId,
    },
    cache: 'no-store',
  });
  if (!resp.ok) return null;
  const body = (await resp.json()) as SingleTenantStatusResponse;
  return (body?.status?.container_url ?? '').replace(/\/+$/, '') || null;
}

export async function getContainerUrl(tenantId: string): Promise<string | null> {
  const cached = cache.get(tenantId);
  if (cached) return cached;
  const pending = inFlight.get(tenantId);
  if (pending) return pending;

  const promise = fetchContainerUrl(tenantId)
    .then((url) => {
      if (url) cache.set(tenantId, url);
      return url;
    })
    .catch(() => null)
    .finally(() => inFlight.delete(tenantId));
  inFlight.set(tenantId, promise);
  return promise;
}

export function invalidateContainerUrl(tenantId: string): void {
  cache.delete(tenantId);
}

/** Tests only. */
export function resetContainerDirectory(): void {
  cache.clear();
  inFlight.clear();
}
